import Close from '@mui/icons-material/Close';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Replay from '@mui/icons-material/Replay';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Link, type LinkDescriptor } from 'react-router';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';

import { CTA } from './CTA.tsx';
import heroMobileAvif from './hero-image-mobile.avif';
import heroMobileWebp from './hero-image-mobile.webp';
import heroAvif from './hero-image.avif';
import heroWebp from './hero-image.webp';
import { useHasEnded } from './use-has-ended.ts';

/** Enhances regular image placeholder to be used in thumbnails */
const imagePlaceholder = (placeholder: string, width: number, height: number) =>
  'data:image/svg+xml,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"><filter id="a"><feGaussianBlur stdDeviation="${
      Math.min(width, height) / 20
    }"/><feColorMatrix values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 0 1"/></filter><image width="100%" height="100%" filter="url(#a)" href="${placeholder}" preserveAspectRatio="xMidYMid slice"/></svg>`,
  );

const lqip =
  'data:image/webp;base64,UklGRkAAAABXRUJQVlA4IDQAAADwAQCdASoQAAkABUB8JbACdGuAAsn0zUAAym+XJt6eaHUqOTvkBGKFoSTF8vN4noq3UAAA';

const links: LinkDescriptor[] = [
  {
    href: heroAvif,
    rel: 'preload',
    as: 'image',
  },
];

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
  displayWarningWhenKIn: string[] | undefined;
}) => {
  return (
    <>
      <div>
        <div className="relative isolate flex min-h-[min(calc(100dvh-var(--top-bar-height)),var(--max-h))] w-full flex-col overflow-hidden text-white [--max-h:850px] sm:[--max-h:1100px] md:[--max-h:1250px] xl:[--max-h:1100px]">
          <div className="mx-auto w-full p-6 lg:px-0">
            <Content
              activeCompetition={props.activeCompetition}
              winners={props.winners}
              displayWarningWhenKIn={props.displayWarningWhenKIn}
            />
          </div>

          {/* Background */}
          <div className="-z-10">
            <picture>
              <source srcSet={heroMobileAvif} type="image/avif" media="(max-width: 639px)" />
              <source srcSet={heroMobileWebp} type="image/webp" media="(max-width: 639px)" />
              <source srcSet={heroAvif} type="image/avif" />
              <source srcSet={heroWebp} type="image/webp" />
              <img
                src={heroWebp}
                alt=""
                className="absolute inset-0 size-full bg-cover bg-center bg-no-repeat object-cover object-bottom sm:object-bottom-right xl:object-center"
                fetchPriority="high"
                loading="eager"
                style={{
                  backgroundImage: `url('${imagePlaceholder(lqip, 16, 9)}')`,
                }}
              />
            </picture>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-white pt-4">
          <div className="container mx-auto max-w-3xl px-5 text-center typography-v1-body4 text-balance">
            <LoanExampleText />
            <br />
            *Privat gjeld og fellesgjeld i borettslag ikke er omfattet av premien. For mer
            informasjon les{' '}
            <Link
              to={routes.SEPTEMBER_2026_COMPETITION_TERMS}
              className="text-v1-primary-100 underline"
            >
              vilkår og betingelser
            </Link>
            .
          </div>
        </div>
      </div>

      <div className="hidden px-4 max-md:block">
        <div className="mx-auto flex max-w-sm">
          <Winner />
        </div>
      </div>
    </>
  );
};

const Winner = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (!video) return;

    if (hasEnded) {
      sendUserPortalEvent('sep26_landing_video_replay_clicked');
      video.currentTime = 0;
      setHasEnded(false);
    } else {
      sendUserPortalEvent('sep26_landing_video_play_clicked');
    }

    void video.play();
  };

  const handleExitFullscreen = () => {
    if (document.fullscreenElement) {
      sendUserPortalEvent('sep26_landing_video_fullscreen_closed');
      void document.exitFullscreen();
    }
  };

  const handleFullscreenClick = () => {
    const container = containerRef.current;
    if (!container) return;

    if (document.fullscreenElement) {
      sendUserPortalEvent('sep26_landing_video_fullscreen_closed');
      void document.exitFullscreen();
    } else {
      sendUserPortalEvent('sep26_landing_video_fullscreen_opened');
      void container.requestFullscreen();
    }
  };

  return (
    <section className="flex flex-1 flex-col items-center gap-4 text-center">
      <h2 className="typography-v1-h3">Blir dette din gjeld?</h2>
      <div
        ref={containerRef}
        className={clsx(
          'relative w-full flex-1 overflow-hidden bg-black',
          !isFullscreen && 'rounded-xl',
        )}
      >
        <video
          ref={videoRef}
          className={clsx('size-full', isFullscreen ? 'object-contain' : 'object-cover')}
          src="https://storage.googleapis.com/ui_step_type_screenshots/0_september2026.mp4"
          playsInline
          disablePictureInPicture
          disableRemotePlayback
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            sendUserPortalEvent('sep26_landing_video_ended');
            setHasEnded(true);
            setIsPlaying(false);
          }}
        />
        {!isPlaying && (
          <button
            type="button"
            onClick={handlePlayClick}
            className="absolute inset-0 flex items-center justify-center bg-black/30 text-white transition-colors hover:bg-black/40"
            aria-label={hasEnded ? 'Spill igjen' : 'Spill av'}
          >
            {hasEnded ? <Replay className="text-6xl!" /> : <PlayArrow className="text-7xl!" />}
          </button>
        )}
        {isFullscreen && (
          <button
            type="button"
            onClick={handleExitFullscreen}
            className="absolute top-2 right-2 rounded-lg bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
            aria-label="Avslutt fullskjerm"
          >
            <Close />
          </button>
        )}
        <button
          type="button"
          onClick={handleFullscreenClick}
          className="absolute right-2 bottom-2 rounded-lg bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
          aria-label={isFullscreen ? 'Avslutt fullskjerm' : 'Fullskjerm'}
        >
          {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
        </button>
      </div>
    </section>
  );
};

const Card = (props: React.ComponentProps<'div'>) => (
  <div
    {...props}
    className={clsx(
      'rounded-2xl bg-v1-background-20 p-4 text-v1-background-100 sm:p-8',
      props.className,
    )}
    style={{
      boxShadow: '2px 4px 40px 10px rgba(199, 205, 209, 0.20)',
      ...props.style,
    }}
  />
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
  displayWarningWhenKIn: string[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col pt-4 pb-64 sm:pb-100 md:pt-8 md:pb-120 xl:pb-20">
      <h1 className="mx-auto mb-6 max-w-5xl text-center text-4xl leading-[1.2] font-bold text-balance sm:mb-12 sm:text-5xl md:text-6xl lg:text-7xl xl:mb-16 2xl:text-8xl">
        <UnderlinedText variant="2" distance="0.2em">
          1 000 000 kr
        </UnderlinedText>{' '}
        til krymping av all gjeld!*
      </h1>

      <div className="mx-auto max-w-4xl">
        <Card className="mb-4 text-center typography-v1-body1">
          <b>16. november</b> får en heldig vinner slettet hele{' '}
          <b className="text-v1-primary-100">1&nbsp;000&nbsp;000&nbsp;kr</b> av gjelden sin. Dette
          gjelder <b className="text-v1-background-200">all type gjeld!*</b>
        </Card>

        <div className="flex flex-col gap-4 md:flex-row">
          <Card className="flex-1">
            <h3 className="mb-2 typography-v1-h3 md:mb-4">For å delta:</h3>
            <ul className="mb-4 flex flex-col gap-3">
              <li className="flex items-center gap-3 typography-v1-body3">
                <div className="grid aspect-square size-8 items-center justify-center rounded-full bg-v1-secondary-100 typography-v1-h4 text-v1-neutral-100">
                  1
                </div>
                Logg inn med bankID
              </li>
              <li className="flex items-center gap-3 typography-v1-body3">
                <div className="grid aspect-square size-8 items-center justify-center rounded-full bg-v1-secondary-100 typography-v1-h4 text-v1-neutral-100">
                  2
                </div>
                <span>
                  Ha minst <b>1 000 kr</b> i rentebærende forbruks- eller kredittkortgjeld
                </span>
              </li>
              <li className="flex items-center gap-3 typography-v1-body3">
                <div className="grid aspect-square size-8 items-center justify-center rounded-full bg-v1-secondary-100 typography-v1-h4 text-v1-neutral-100">
                  3
                </div>
                Søk, se tilbud og delta
              </li>
            </ul>

            <Link
              to={routes.SEPTEMBER_2026_COMPETITION_TERMS}
              className="mb-6 block typography-v1-body4 underline"
            >
              Vilkår og betingelser for deltagelse
            </Link>

            {ended && (
              <p className="-mt-4 mb-4 typography-v1-h4">
                <span>Denne trekningen er avsluttet. </span>
                <button
                  onClick={() => setIsOpen(true)}
                  className="mb-0.5 ml-1 font-semibold text-[#18A0FB] underline"
                >
                  Se vinneren her
                </button>
              </p>
            )}

            <CTA
              className="w-full"
              activeCompetition={props.activeCompetition}
              displayWarningWhenKIn={props.displayWarningWhenKIn}
              onClick={() => {
                sendUserPortalEvent('sep26_competition_cta_top_clicked');
              }}
            />
          </Card>

          <Card className="hidden flex-1 flex-col gap-4 pt-5! md:flex">
            <Winner />
          </Card>
        </div>

        {props.winners && (
          <CompetitionWinnersDialog
            open={isOpen}
            onClose={() => setIsOpen(false)}
            winners={props.winners}
          />
        )}
      </div>
    </div>
  );
};

TopSection.links = links;
